import { socket } from './socket.js';
import { state } from './state.js';

// Cache des emojis custom accessibles au rendu
// _userEmojis  : Map<nom, {id, url}>  — collection perso, dispo partout
// _sphereEmojis: Map<sphereId, Map<nom, {id, url}>> — par sphère
const _userEmojis   = new Map();
const _sphereEmojis = new Map();

// ── Chargement ────────────────────────────────────────────────────────────────

export function chargerUserEmojis() {
    socket.emit('get-user-emojis');
}

export function chargerSphereEmojis(sphereId) {
    if (!sphereId) return;
    socket.emit('get-sphere-emojis', { sphereId });
}

socket.on('user-emojis', ({ emojis }) => {
    _userEmojis.clear();
    emojis.forEach(e => _userEmojis.set(e.nom, { id: e.id, url: e.url }));
    _notifierChangement();
});

socket.on('user-emoji-added', ({ emoji }) => {
    _userEmojis.set(emoji.nom, { id: emoji.id, url: emoji.url });
    _notifierChangement();
});

socket.on('user-emoji-deleted', ({ emojiId }) => {
    for (const [nom, e] of _userEmojis) {
        if (e.id === emojiId) { _userEmojis.delete(nom); break; }
    }
    _notifierChangement();
});

socket.on('sphere-emojis', ({ sphereId, emojis }) => {
    const map = new Map();
    emojis.forEach(e => map.set(e.nom, { id: e.id, url: e.url }));
    _sphereEmojis.set(sphereId, map);
    _notifierChangement();
});

socket.on('sphere-emoji-added', ({ sphereId, emoji }) => {
    if (!_sphereEmojis.has(sphereId)) _sphereEmojis.set(sphereId, new Map());
    _sphereEmojis.get(sphereId).set(emoji.nom, { id: emoji.id, url: emoji.url });
    _notifierChangement();
});

socket.on('sphere-emoji-deleted', ({ sphereId, emojiId }) => {
    const map = _sphereEmojis.get(sphereId);
    if (!map) return;
    for (const [nom, e] of map) {
        if (e.id === emojiId) { map.delete(nom); break; }
    }
    _notifierChangement();
});

// ── Résolution d'un :nom: ─────────────────────────────────────────────────────

export function resoudreEmoji(nom, sphereId) {
    if (_userEmojis.has(nom)) return _userEmojis.get(nom).url;
    if (sphereId) {
        const map = _sphereEmojis.get(sphereId);
        if (map && map.has(nom)) return map.get(nom).url;
    }
    return null;
}

// Applique la substitution :nom: → <img> dans un texte brut.
// À appeler AVANT marked.parse() pour éviter que le markdown ne casse les patterns.
export function appliquerEmojisCustom(texte, sphereId) {
    if (!texte || (_userEmojis.size === 0 && !_sphereEmojis.has(sphereId))) return texte;
    return texte.replace(/:([a-z0-9_]{1,32}):/g, (match, nom) => {
        const url = resoudreEmoji(nom, sphereId);
        if (!url) return match;
        return `<img src="${url}" class="chat-emoji-custom" alt=":${nom}:" title=":${nom}:" loading="lazy">`;
    });
}

// ── Accesseurs pour le picker ─────────────────────────────────────────────────

export function getUserEmojis() {
    return [..._userEmojis.entries()].map(([nom, e]) => ({ nom, ...e }));
}

export function getSphereEmojis(sphereId) {
    if (!sphereId || !_sphereEmojis.has(sphereId)) return [];
    return [..._sphereEmojis.get(sphereId).entries()].map(([nom, e]) => ({ nom, ...e }));
}

// ── Callbacks de mise à jour du picker ───────────────────────────────────────

const _listeners = new Set();
export function onEmojisChange(fn) { _listeners.add(fn); }
export function offEmojisChange(fn) { _listeners.delete(fn); }
function _notifierChangement() { _listeners.forEach(fn => fn()); }
